import { useEffect, useMemo } from 'react';
import { Button, Form, Modal, ModalBody, ModalFooter, ModalHeader, Table } from 'reactstrap';
import { toast } from 'react-toastify';
import { FormProvider, useForm } from 'react-hook-form';
import FormInput from '@/components/forms/FormInput';
import { GlobalSettingsDto, SettingCategory, SettingType } from '@repo/common-types';
import {
  useGlobalSettingsByCategory,
  useTournamentSettings,
  useUpdateTournamentSetting,
} from '@/services/settings/useSettingsQueries';

interface EffectiveSetting extends GlobalSettingsDto {
  isOverridden: boolean;
}

interface TournamentAlertsModalProps {
  tournamentId: string;
  isOpen: boolean;
  onClose: () => void;
}

const TournamentAlertsModal = ({ tournamentId, isOpen, onClose }: TournamentAlertsModalProps) => {
  // React Query hooks
  const {
    data: globalSettings = [],
    isLoading: isLoadingGlobal,
    error: globalError,
  } = useGlobalSettingsByCategory(SettingCategory.TOURNAMENT, isOpen);

  const {
    data: tournamentSettingsData = [],
    isLoading: isLoadingTournament,
    error: tournamentError,
  } = useTournamentSettings(tournamentId, isOpen && !!tournamentId);

  const updateTournamentSettingMutation = useUpdateTournamentSetting();

  const isLoading = isLoadingGlobal || isLoadingTournament;
  const isSaving = updateTournamentSettingMutation.isPending;

  // React Hook Form setup
  const methods = useForm({
    mode: 'onChange',
  });

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { isDirty },
  } = methods;

  // Compute effective settings using useMemo
  const effectiveSettings: EffectiveSetting[] = useMemo(() => {
    if (!globalSettings.length) return [];

    return globalSettings.map((globalSetting) => {
      // Check if this setting has a tournament override
      const tournamentValue = tournamentSettingsData?.find((ts) => ts.key === globalSetting.key)?.value;
      const hasOverride = tournamentValue !== undefined;
      const effectiveValue = tournamentValue || globalSetting.value;

      return {
        ...globalSetting,
        isOverridden: hasOverride,
        value: effectiveValue,
      };
    });
  }, [globalSettings, tournamentSettingsData]);

  // Initialize form values when effective settings change
  useEffect(() => {
    if (effectiveSettings.length > 0) {
      console.log('effectiveSettings', effectiveSettings);
      const formValues = effectiveSettings.reduce(
        (acc, setting) => {
          acc[setting.key] = setting.value;
          return acc;
        },
        {} as Record<string, string>,
      );

      reset(formValues);
    }
  }, [effectiveSettings, reset]);

  const onSubmit = async (formData: Record<string, string>) => {
    const changedSettings = Object.entries(formData).filter(([key, value]) => {
      const originalSetting = effectiveSettings.find((s) => s.key === key);
      return originalSetting && value !== originalSetting.value;
    });

    if (changedSettings.length === 0) {
      toast.info('No changes to save');
      return;
    }

    try {
      // Process each changed setting
      const promises = changedSettings.map(async ([key, value]) => {
        const originalSetting = effectiveSettings.find((s) => s.key === key);
        if (!originalSetting) return;

        // Only update if value differs from the current effective value
        return updateTournamentSettingMutation.mutateAsync({
          tournamentId,
          key,
          data: {
            value,
            type: originalSetting?.type as SettingType,
          },
        });
      });

      await Promise.all(promises);
    } catch (error) {
      console.error('Error saving settings:', error);
      // Error toast is handled by the mutation hook
    }
  };

  const formatKey = (key: string) => {
    return key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase());
  };

  const hasChanges = () => {
    return isDirty;
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} className="modal-xl" centered>
      <ModalHeader toggle={onClose}>Tournament Settings</ModalHeader>
      <ModalBody style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {isLoading ? (
          <div className="py-4 text-center">
            <div className="spinner-border" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        ) : globalError || tournamentError ? (
          <div className="py-4 text-center">
            <div className="alert alert-danger">Failed to load settings. Please try again.</div>
          </div>
        ) : (
          <FormProvider {...methods}>
            <Form id="tournament-settings-form" onSubmit={handleSubmit(onSubmit)}>
              <div className="mb-3">
                <p className="text-muted">
                  Configure tournament-specific settings. You can override global settings for this tournament or use
                  the default values.
                </p>
              </div>

              <Table responsive>
                <thead>
                  <tr>
                    <th>Setting</th>
                    <th>Value</th>
                  </tr>
                </thead>
                <tbody>
                  {effectiveSettings.map((setting) => (
                    <tr key={setting.key}>
                      <td>
                        <strong>{formatKey(setting.key)}</strong>
                        {setting.description && <div className="text-muted small">{setting.description}</div>}
                      </td>
                      <td className="w-100 d-flex flex-column align-items-start justify-content-start gap-1">
                        <div className="w-100">
                          <FormInput name={setting.key} control={control} type="text" />
                          <p className="text-muted small mb-0">
                            Default: {globalSettings.find((gs) => gs.key === setting.key)?.value}
                          </p>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Form>
          </FormProvider>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          color="primary"
          type="submit"
          form="tournament-settings-form"
          disabled={!hasChanges() || isSaving || isLoading}
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default TournamentAlertsModal;
